import React, { useState, useEffect } from "react";
import {
  Grid,
  Paper,
  Box,
  Typography,
  Chip,
  Dialog,
  IconButton,
  TextField,
  Button,
  Collapse,
} from "@mui/material";
import * as actions from "./actions";
import { upload } from "../Common/actions";
import { API_URL } from "../../constants";
import { toastError, toastSuccess } from "../../utils";
import CustomAddModal from "../Common/CustomAddModal";
import ConfirmModal from "../Common/CommonModal";
import EmptyState from "../../components/EmptyState";
import {
  Zap,
  Calendar,
  Image,
  X,
  ChevronDown,
  ChevronUp,
  ImageOff,
  ShieldOff,
  Upload,
} from "lucide-react";

export default function InstantPromotions(props) {
  const [promotions, setPromotions] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    message: "",
    title: "",
    onConfirm: null,
  });
  const [newPromo, setNewPromo] = useState({
    heading: "",
    image: null,
    imagePreview: null,
    expire_at: "",
    link: "",
  });
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    document.title = "IP | Instant Promotions";
    return () => {
      document.title = "Fronic Media Admin";
    };
  }, []);

  useEffect(() => {
    fetchPromotions();
  }, []);

  const fetchPromotions = () => {
    props.setLoader(true);
    actions
      .getInstantPromotionsAdmin()
      .then((res) => {
        props.setLoader(false);
        setPromotions(res.body || []);
      })
      .catch((err) => {
        props.setLoader(false);
        toastError(err?.message || "Failed to load promotions");
      });
  };

  const hasActivePromo = promotions.some((p) => p.status === "active");
  const activePromo = promotions.find((p) => p.status === "active");
  const historyPromos = promotions
    .filter((p) => p.status === "inactive")
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setNewPromo({
        ...newPromo,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith("image/")) {
      setNewPromo({
        ...newPromo,
        image: file,
        imagePreview: URL.createObjectURL(file),
      });
    }
  };

  const handleAddPromotion = async () => {
    if (!newPromo.heading) {
      toastError("Heading is required.");
      return;
    }
    if (!newPromo.image) {
      toastError("Image is required.");
      return;
    }
    if (!newPromo.expire_at) {
      toastError("Expiry date is required.");
      return;
    }

    props.setLoader(true);

    try {
      let imageUrl = null;
      if (newPromo.image) {
        const uploadRes = await upload(newPromo.image);
        imageUrl = `${API_URL}/${uploadRes.body.filename}`;
      }

      const payload = {
        heading: newPromo.heading,
        image: imageUrl,
        expire_at: newPromo.expire_at,
      };
      if (newPromo.link.trim()) {
        payload.link = newPromo.link.trim();
      }
      const res = await actions.addInstantPromotionAdmin(payload);

      props.setLoader(false);
      toastSuccess(res.message || "Promotion added successfully");
      setAddModalOpen(false);
      setNewPromo({ heading: "", image: null, imagePreview: null, expire_at: "", link: "" });
      fetchPromotions();
    } catch (err) {
      props.setLoader(false);
      toastError(err?.message || "Failed to add promotion");
    }
  };

  const handleDeactivate = (item) => {
    setConfirmModal({
      open: true,
      title: "Deactivate Promotion",
      message:
        "Are you sure you want to deactivate this promotion? The image will be kept for history.",
      onConfirm: () => {
        setConfirmModal({ ...confirmModal, open: false });
        props.setLoader(true);
        actions
          .deactivateInstantPromotionAdmin({
            instant_promotion_id: item.instant_promotion_id,
          })
          .then((res) => {
            props.setLoader(false);
            toastSuccess(res.message || "Promotion deactivated");
            fetchPromotions();
          })
          .catch((err) => {
            props.setLoader(false);
            toastError(err?.message || "Failed to deactivate");
          });
      },
    });
  };

  const handleRemoveAndDeactivate = (item) => {
    setConfirmModal({
      open: true,
      title: "Remove Image & Deactivate",
      message:
        "Are you sure? This will remove the image from the database and deactivate the promotion. This action cannot be undone.",
      onConfirm: () => {
        setConfirmModal({ ...confirmModal, open: false });
        props.setLoader(true);
        actions
          .removeAndDeactivateInstantPromotionAdmin({
            instant_promotion_id: item.instant_promotion_id,
          })
          .then((res) => {
            props.setLoader(false);
            toastSuccess(res.message || "Image removed & promotion deactivated");
            fetchPromotions();
          })
          .catch((err) => {
            props.setLoader(false);
            toastError(err?.message || "Failed to remove & deactivate");
          });
      },
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    const d = new Date(dateStr);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const renderPromoCard = (item, isActive) => (
    <Paper
      key={item.instant_promotion_id}
      sx={{
        backgroundColor: "#1E2130",
        border: isActive
          ? "1px solid rgba(16, 185, 129, 0.3)"
          : "1px solid #2E3247",
        borderRadius: "12px",
        overflow: "hidden",
        mb: 2,
      }}
    >
      <Box display="flex" gap={2} p={2}>
        {/* Image thumbnail */}
        <Box
          sx={{
            width: 120,
            height: 120,
            borderRadius: "8px",
            overflow: "hidden",
            flexShrink: 0,
            backgroundColor: "#141620",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: item.image ? "pointer" : "default",
          }}
          onClick={() => item.image && setPreviewImage(item.image)}
        >
          {item.image ? (
            <img
              src={item.image}
              alt=""
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
          ) : (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              gap={0.5}
            >
              <Image size={24} color="#64748B" />
              <Typography sx={{ fontSize: "0.7rem", color: "#64748B" }}>
                No Image
              </Typography>
            </Box>
          )}
        </Box>

        {/* Content */}
        <Box flex={1} display="flex" flexDirection="column" gap={1}>
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography
              sx={{
                color: "#F1F5F9",
                fontSize: "1rem",
                fontWeight: 600,
                flex: 1,
              }}
            >
              {item.heading}
            </Typography>
            <Chip
              label={item.status === "active" ? "Active" : "Inactive"}
              size="small"
              sx={{
                backgroundColor:
                  item.status === "active"
                    ? "rgba(16, 185, 129, 0.12)"
                    : "rgba(100, 116, 139, 0.12)",
                color:
                  item.status === "active" ? "#10B981" : "#64748B",
                border: `1px solid ${
                  item.status === "active"
                    ? "rgba(16, 185, 129, 0.3)"
                    : "rgba(100, 116, 139, 0.3)"
                }`,
                fontWeight: 600,
                fontSize: "0.72rem",
                height: 24,
              }}
            />
          </Box>

          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <Box display="flex" alignItems="center" gap={0.5}>
              <Calendar size={13} color="#F59E0B" />
              <Typography
                sx={{
                  fontSize: "0.8rem",
                  color: "#F59E0B",
                  fontWeight: 500,
                  fontFamily: "monospace",
                }}
              >
                Expires: {formatDate(item.expire_at)}
              </Typography>
            </Box>
            {item.created_at && (
              <Typography
                sx={{
                  fontSize: "0.75rem",
                  color: "#64748B",
                }}
              >
                Created: {formatDate(item.created_at)}
              </Typography>
            )}
          </Box>

          {/* Action buttons for active promo */}
          {isActive && (
            <Box display="flex" gap={1} mt={0.5}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ShieldOff size={14} />}
                onClick={() => handleDeactivate(item)}
                sx={{
                  color: "#F59E0B",
                  borderColor: "rgba(245, 158, 11, 0.3)",
                  fontSize: "0.75rem",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#F59E0B",
                    backgroundColor: "rgba(245, 158, 11, 0.08)",
                  },
                }}
              >
                Deactivate
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<ImageOff size={14} />}
                onClick={() => handleRemoveAndDeactivate(item)}
                sx={{
                  color: "#EF4444",
                  borderColor: "rgba(239, 68, 68, 0.3)",
                  fontSize: "0.75rem",
                  textTransform: "none",
                  "&:hover": {
                    borderColor: "#EF4444",
                    backgroundColor: "rgba(239, 68, 68, 0.08)",
                  },
                }}
              >
                Remove Image & Deactivate
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Grid container>
      {/* Header */}
      <Grid item xs={12}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Chip
              icon={<Zap size={14} />}
              label={`${promotions.length} Total`}
              sx={{
                backgroundColor: "rgba(108, 99, 255, 0.12)",
                color: "#6C63FF",
                border: "1px solid rgba(108, 99, 255, 0.25)",
                fontWeight: 600,
                fontSize: "0.85rem",
                height: 34,
                "& .MuiChip-icon": { color: "#6C63FF" },
              }}
            />
          </Box>
          {!hasActivePromo && (
            <Button
              variant="outlined"
              onClick={() => setAddModalOpen(true)}
              sx={{
                color: "#6C63FF",
                borderColor: "rgba(108, 99, 255, 0.4)",
                textTransform: "none",
                fontWeight: 600,
                "&:hover": {
                  borderColor: "#6C63FF",
                  backgroundColor: "rgba(108, 99, 255, 0.08)",
                },
              }}
            >
              + Add Promotion
            </Button>
          )}
        </Box>
      </Grid>

      {/* Current Promotion */}
      <Grid item xs={12}>
        <Typography
          sx={{
            fontSize: "0.85rem",
            fontWeight: 600,
            color: "#F1F5F9",
            mb: 1.5,
            letterSpacing: "0.02em",
          }}
        >
          Current Promotion
        </Typography>
        {activePromo ? (
          renderPromoCard(activePromo, true)
        ) : (
          <Paper
            sx={{
              backgroundColor: "#1E2130",
              border: "1px solid #2E3247",
              borderRadius: "12px",
              p: 4,
              mb: 2,
              textAlign: "center",
            }}
          >
            <Zap size={32} color="#4A5068" style={{ marginBottom: 8 }} />
            <Typography sx={{ color: "#64748B", fontSize: "0.9rem" }}>
              No active promotion.{" "}
              {!hasActivePromo && "Click \"+ Add Promotion\" to create one."}
            </Typography>
          </Paper>
        )}
      </Grid>

      {/* History Section */}
      <Grid item xs={12} sx={{ mt: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            cursor: "pointer",
            mb: 1.5,
            userSelect: "none",
          }}
          onClick={() => setHistoryOpen(!historyOpen)}
        >
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 600,
              color: "#F1F5F9",
              letterSpacing: "0.02em",
            }}
          >
            History ({historyPromos.length})
          </Typography>
          {historyOpen ? (
            <ChevronUp size={18} color="#8B92A5" />
          ) : (
            <ChevronDown size={18} color="#8B92A5" />
          )}
        </Box>
        <Collapse in={historyOpen} timeout={300}>
          {historyPromos.length > 0 ? (
            historyPromos.map((item) => renderPromoCard(item, false))
          ) : (
            <Paper
              sx={{
                backgroundColor: "#1E2130",
                border: "1px solid #2E3247",
                borderRadius: "12px",
                p: 3,
                textAlign: "center",
              }}
            >
              <Typography sx={{ color: "#64748B", fontSize: "0.85rem" }}>
                No history yet.
              </Typography>
            </Paper>
          )}
        </Collapse>
      </Grid>

      {/* Add Promotion Modal */}
      <CustomAddModal
        open={addModalOpen}
        title="Add Instant Promotion"
        saveLabel="Create"
        onSubmitConfirm={handleAddPromotion}
        handleClose={() => {
          setAddModalOpen(false);
          setNewPromo({
            heading: "",
            image: null,
            imagePreview: null,
            expire_at: "",
          });
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', mt: 0.5 }}>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mb: '4px' }}>Heading</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="Promotion heading"
              value={newPromo.heading}
              onChange={(e) =>
                setNewPromo({ ...newPromo, heading: e.target.value })
              }
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mb: '4px' }}>Promotion Image</Typography>
            <input
              type="file"
              accept="image/*"
              id="promo-image-upload"
              style={{ display: "none" }}
              onChange={handleImageSelect}
            />
            {newPromo.imagePreview ? (
              <Box
                sx={{
                  position: 'relative',
                  border: '1px solid var(--border)',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  display: 'inline-block',
                }}
              >
                <img
                  src={newPromo.imagePreview}
                  alt="Preview"
                  style={{
                    maxWidth: '100%',
                    maxHeight: 140,
                    display: 'block',
                    objectFit: 'contain',
                  }}
                />
                <IconButton
                  size="small"
                  onClick={() => setNewPromo({ ...newPromo, image: null, imagePreview: null })}
                  sx={{
                    position: 'absolute',
                    top: 4,
                    right: 4,
                    backgroundColor: 'rgba(0,0,0,0.6)',
                    color: '#F1F5F9',
                    width: 22,
                    height: 22,
                    '&:hover': { backgroundColor: 'rgba(0,0,0,0.8)' },
                  }}
                >
                  <X size={12} />
                </IconButton>
              </Box>
            ) : (
              <label htmlFor="promo-image-upload">
                <Box
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  sx={{
                    border: dragging ? '1.5px dashed #6C63FF' : '1.5px dashed var(--border)',
                    borderRadius: '6px',
                    py: 3.5,
                    px: 2,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.8,
                    cursor: 'pointer',
                    backgroundColor: dragging ? 'rgba(108, 99, 255, 0.06)' : 'transparent',
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      borderColor: '#6C63FF',
                      backgroundColor: 'rgba(108, 99, 255, 0.04)',
                    },
                  }}
                >
                  <Upload size={18} color={dragging ? '#6C63FF' : '#64748B'} />
                  <Typography sx={{ fontSize: '0.78rem', color: '#94A3B8' }}>
                    Drop image here or <span style={{ color: '#6C63FF', fontWeight: 600 }}>browse</span>
                  </Typography>
                </Box>
              </label>
            )}
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mb: '4px' }}>Expire At</Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              type="datetime-local"
              value={newPromo.expire_at}
              onChange={(e) =>
                setNewPromo({ ...newPromo, expire_at: e.target.value })
              }
              InputLabelProps={{ shrink: true }}
            />
          </Box>
          <Box>
            <Typography sx={{ fontSize: '0.8rem', color: '#94A3B8', mb: '4px' }}>Link <span style={{ color: '#64748B', fontWeight: 400 }}>(Optional)</span></Typography>
            <TextField
              variant="outlined"
              size="small"
              fullWidth
              placeholder="https://example.com"
              value={newPromo.link}
              onChange={(e) =>
                setNewPromo({ ...newPromo, link: e.target.value })
              }
            />
          </Box>
        </Box>
      </CustomAddModal>

      {/* Confirmation Modal */}
      <ConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onSubmitConfirm={confirmModal.onConfirm}
        handleClose={() =>
          setConfirmModal({ ...confirmModal, open: false })
        }
      />

      {/* Image Preview Dialog */}
      <Dialog
        open={!!previewImage}
        onClose={() => setPreviewImage(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            backgroundColor: "transparent",
            boxShadow: "none",
            overflow: "visible",
            position: "relative",
          },
        }}
        slotProps={{
          backdrop: { sx: { backgroundColor: "rgba(0, 0, 0, 0.85)" } },
        }}
      >
        <IconButton
          onClick={() => setPreviewImage(null)}
          sx={{
            position: "absolute",
            top: -16,
            right: -16,
            zIndex: 1,
            backgroundColor: "rgba(30, 33, 48, 0.9)",
            color: "#F1F5F9",
            border: "1px solid #2E3247",
            "&:hover": { backgroundColor: "#2E3247" },
            width: 36,
            height: 36,
          }}
        >
          <X size={18} />
        </IconButton>
        {previewImage && (
          <img
            src={previewImage}
            alt="Preview"
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
              borderRadius: 8,
              display: "block",
            }}
          />
        )}
      </Dialog>
    </Grid>
  );
}
